import { useMemo } from "react"
import { useAuth } from "@/features/auth/AuthContext"
import { APPLICATION_LOGS_VIEW } from "@/features/admin/applicationLogsPermission"
import {
  AUDIT_ASSOCIATIONS_VIEW,
  AUDIT_DEPOSITS_VIEW,
  AUDIT_EVENTS_VIEW,
} from "@/features/admin/auditPermissions"
import {
  CREATIVE_SELF_DEFINITION_MANAGE_CATALOG,
  CREATIVE_SELF_DEFINITION_MODERATE,
} from "@/features/admin/creativeSelfDefinitionPermission"
import { ASSOCIATIONS_POLICY } from "@/features/associations/associationsPermissions"

export type AdminAccess = {
  canManageRoles: boolean
  canManageUsers: boolean
  canModerateCreativeSelfDefinition: boolean
  /** Список заявок на новые роли + approve/reject */
  canModerateCreativeRoleSuggestions: boolean
  /** Каталог типовых ролей (CRUD описаний, soft-delete) */
  canManageCreativeSelfDefinitionCatalog: boolean
  canManageAssociations: boolean
  canVerifyAssociations: boolean
  canListUnverifiedAssociations: boolean
  canViewApplicationLogs: boolean
  canViewAuditDeposits: boolean
  canViewAuditEvents: boolean
  canViewAuditAssociations: boolean
  canViewBusinessAudit: boolean
  hasAnyAdminAccess: boolean
}

/**
 * Проверка прав для раздела /portal/admin:
 * - Identity (`AbpIdentity.Roles` / `AbpIdentity.Users`);
 * - Ассоциации (`LeninkaArt.Associations.Manage` / `.Verify` / `.ListUnverified`);
 * - Логи приложения (`LeninkaArt.ApplicationLogs.View`);
 * - Бизнес-аудит (`LeninkaArt.Audit.*.View`).
 */
export const useAdminAccess = (): AdminAccess => {
  const { grantedPolicies, isAdmin } = useAuth()
  return useMemo(() => {
    const policies = grantedPolicies ?? {}
    const canManageRoles = Boolean(policies["AbpIdentity.Roles"]) || isAdmin
    const canManageUsers = Boolean(policies["AbpIdentity.Users"]) || isAdmin
    const canModerateCreativeRoleSuggestions =
      Boolean(policies[CREATIVE_SELF_DEFINITION_MODERATE]) || isAdmin
    const canManageCreativeSelfDefinitionCatalog =
      Boolean(policies[CREATIVE_SELF_DEFINITION_MANAGE_CATALOG]) || isAdmin
    const canModerateCreativeSelfDefinition =
      Boolean(policies[CREATIVE_SELF_DEFINITION_MODERATE]) ||
      Boolean(policies[CREATIVE_SELF_DEFINITION_MANAGE_CATALOG]) ||
      isAdmin
    const canManageAssociations = Boolean(policies[ASSOCIATIONS_POLICY.manage]) || isAdmin
    const canVerifyAssociations =
      canManageAssociations || Boolean(policies[ASSOCIATIONS_POLICY.verify])
    const canListUnverifiedAssociations =
      canManageAssociations ||
      canVerifyAssociations ||
      Boolean(policies[ASSOCIATIONS_POLICY.listUnverified])
    const canViewApplicationLogs = Boolean(policies[APPLICATION_LOGS_VIEW]) || isAdmin
    const canViewAuditDeposits = Boolean(policies[AUDIT_DEPOSITS_VIEW]) || isAdmin
    const canViewAuditEvents = Boolean(policies[AUDIT_EVENTS_VIEW]) || isAdmin
    const canViewAuditAssociations = Boolean(policies[AUDIT_ASSOCIATIONS_VIEW]) || isAdmin
    const canViewBusinessAudit =
      canViewAuditDeposits || canViewAuditEvents || canViewAuditAssociations
    return {
      canManageRoles,
      canManageUsers,
      canModerateCreativeSelfDefinition,
      canModerateCreativeRoleSuggestions,
      canManageCreativeSelfDefinitionCatalog,
      canManageAssociations,
      canVerifyAssociations,
      canListUnverifiedAssociations,
      canViewApplicationLogs,
      canViewAuditDeposits,
      canViewAuditEvents,
      canViewAuditAssociations,
      canViewBusinessAudit,
      hasAnyAdminAccess:
        canManageRoles ||
        canManageUsers ||
        canModerateCreativeSelfDefinition ||
        canManageAssociations ||
        canVerifyAssociations ||
        canListUnverifiedAssociations ||
        canViewApplicationLogs ||
        canViewBusinessAudit,
    }
  }, [grantedPolicies, isAdmin])
}
